/**
 * County select — atlas `SearchSelect` (dropdown + type-to-filter search)
 * backed by `/v2/counties/`, scoped to the row's State.
 *
 * The list is limited to the selected State's counties; the SearchSelect's
 * own search box filters that list. The STORED value is the county FIPS
 * code (id) and the NAME is shown. The control stays enabled even before a
 * State is chosen (the list is just empty until then) — pending a flow
 * decision on whether County should depend on State at all.
 *
 * Rows the FE didn't write carry the county as free text instead of a
 * FIPS: the Pricing Model import (OR-1511) lands the sheet's cell verbatim
 * ("Spartanburg County"). `resolveCountyKey` matches either form against
 * the state's list so those rows show their county instead of the
 * placeholder. Re-picking still writes the FIPS.
 */
import { useEffect, useMemo, useState } from "react";
import { SearchSelect } from "@tricura/atlas-ui";
import { listCountiesByState, resolveCountyKey, type County } from "../api/endpoints/counties";

/** Per-state cache — the lists are small and stable for a session. */
const stateCache = new Map<string, County[]>();

interface CountySelectProps {
    /** Stored value — the county FIPS code (id), or a county name for
     *  rows written outside the FE (see module header). */
    value: string;
    /** Fired with the selected county's FIPS code. */
    onChange: (fips: string) => void;
    /** The row's State (2-letter code) — filters the county list. */
    state: string;
    ariaLabel: string;
    placeholder?: string;
    /** Custom attribute for E2E testing. */
    "data-autotest"?: string;
}

export function CountySelect({ value, onChange, state, ariaLabel, placeholder = "Select county", "data-autotest": dataAutotest }: CountySelectProps) {
    const [counties, setCounties] = useState<County[]>(() => (state ? (stateCache.get(state) ?? []) : []));

    useEffect(() => {
        if (!state) {
            setCounties([]);
            return;
        }
        const cached = stateCache.get(state);
        if (cached) {
            setCounties(cached);
            return;
        }
        let cancelled = false;
        void listCountiesByState(state)
            .then((rows) => {
                if (cancelled) return;
                stateCache.set(state, rows);
                setCounties(rows);
            })
            .catch(() => {
                if (!cancelled) setCounties([]);
            });
        return () => {
            cancelled = true;
        };
    }, [state]);

    const items = useMemo(() => counties.map((c) => ({ id: c.fips, label: c.name })), [counties]);
    const selectedKey = useMemo(() => resolveCountyKey(value, counties), [value, counties]);

    return (
        <SearchSelect
            size="sm"
            aria-label={ariaLabel}
            placeholder={placeholder}
            selectedKey={selectedKey}
            onSelectionChange={(k) => onChange((k as string) ?? "")}
            items={items}
            popoverWidthMul={1.4}
            data-autotest={dataAutotest}
        >
            {(item) => (
                <SearchSelect.Item id={item.id} data-autotest={dataAutotest ? `${dataAutotest}-option-${item.label}` : undefined}>
                    {item.label}
                </SearchSelect.Item>
            )}
        </SearchSelect>
    );
}
